import axios, { AxiosRequestConfig } from 'axios';
import { API_CONFIG } from '../config/env';
import { anonymousPayerService } from './anonymousPayerService';
import { InvoiceDetails, MerchantReferences } from './paymentLinkService';

export interface PublicCheckoutOption {
  sessionId: string;
  provider: string;
  paymentMethod: string;
  url: string;
  inlineUrl?: string;
  status: string;
  isEnabled: boolean;
  isRecommended: boolean;
  supportsInlineEmbed: boolean;
}

export interface PublicBuyerContext {
  payerCountryCode?: string | null;
  payerSubdivisionCode?: string | null;
  anonymousPayerId?: string | null;
}

export interface PublicFiatOptionsResult {
  buyerCountryCode?: string | null;
  buyerSubdivisionCode?: string | null;
  fiatWidget?: PublicFiatWidget | null;
  fiatOptions: PublicCheckoutOption[];
}

export interface PublicFiatProviderIssueRequest {
  sessionId?: string | null;
  provider: string;
  paymentMethod?: string | null;
  reasonCode?: string | null;
}

export interface PublicFiatWidget {
  provider: string;
  mode: string;
  launchUrl: string;
  inlineUrl: string;
  supportsInlineEmbed: boolean;
  sourceAmount: number;
  sourceCurrency: string;
  destinationCurrency: string;
  destinationNetwork: string;
  destinationAddress: string;
}

export interface PublicCryptoSwapOption {
  provider: string;
  providerKind?: string;
  url: string;
  apiUrl: string;
  integratorId: string;
  destinationAddress: string;
  toTokenSymbol: string;
  toTokenAddress: string;
  toChain: string;
  toChainId: number;
  widgetMode?: string;
  supportsEmbeddedWidget?: boolean;
  supportsBackendQuoteProxy?: boolean;
  sourceFamilyHints?: string[];
  isEnabled: boolean;
  unavailableReason?: string | null;
}

export interface PublicCryptoSwapLockedTarget {
  recipientAddress: string;
  chain: string;
  chainId: number;
  tokenSymbol: string;
  tokenAddress: string;
}

export interface PublicCryptoSwapUi {
  mode: string;
  supportsEmbeddedWidget: boolean;
  supportsQuoteProxy: boolean;
  hostedPath: string;
  title: string;
  description: string;
}

export interface SimpleSwapEstimateQuery {
  tickerFrom: string;
  networkFrom: string;
  tickerTo: string;
  networkTo: string;
  amount: string;
  fixed?: boolean;
  reverse?: boolean;
  customFee?: string;
}

export interface SimpleSwapRangeQuery {
  tickerFrom: string;
  networkFrom: string;
  tickerTo: string;
  networkTo: string;
  fixed?: boolean;
  reverse?: boolean;
}

export interface SimpleSwapAvailabilityQuery extends SimpleSwapEstimateQuery {}

export interface SimpleSwapCreateExchangeRequest {
  tickerFrom: string;
  networkFrom: string;
  tickerTo: string;
  networkTo: string;
  amount: string;
  addressTo: string;
  fixed?: boolean;
  reverse?: boolean;
  customFee?: string | null;
  extraIdTo?: string | null;
  userRefundAddress?: string | null;
  userRefundExtraId?: string | null;
  rateId?: string | null;
}

export interface SimpleSwapApiEnvelope<T> {
  result: T;
  traceId?: string;
}

export interface SimpleSwapEstimateResult {
  estimatedAmount?: string;
  rateId?: string;
  validUntil?: string;
  [key: string]: unknown;
}

export interface SimpleSwapRangeResult {
  min?: string;
  max?: string;
  [key: string]: unknown;
}

export interface SimpleSwapAvailabilityResult {
  available?: boolean;
  [key: string]: unknown;
}

export interface SimpleSwapExchangeResult {
  publicId?: string;
  status?: string;
  redirectUrl?: string;
  type?: string;
  addressFrom?: string;
  addressTo?: string;
  amountFrom?: string;
  amountTo?: string;
  tickerFrom?: string;
  tickerTo?: string;
  networkFrom?: string;
  networkTo?: string;
  [key: string]: unknown;
}

export interface SimpleSwapCurrencyResult {
  ticker?: string;
  symbol?: string;
  network?: string;
  chain?: string;
  name?: string;
  enabled?: boolean;
  isAvailableFloat?: boolean;
  isAvailableFixed?: boolean;
  [key: string]: unknown;
}

export type PublicInvoiceDetails = InvoiceDetails;

export interface PublicPaymentPage {
  id: string;
  fiatCurrency: string;
  fiatAmount: number;
  cryptoCurrency: string;
  cryptoNetwork: string;
  quotedCryptoAmount?: number | null;
  quoteCreatedAt?: string | null;
  quoteExpiresAt?: string | null;
  uniqueWalletAddress: string;
  disposableAddress?: string;
  merchantReferences?: MerchantReferences | null;
  isInvoice: boolean;
  invoice?: PublicInvoiceDetails | null;
  status: string;
  lifecycleStatus?: string;
  displayStatus: string;
  paymentSource: string;
  provider?: string;
  cryptoAmount?: number | null;
  receivedCryptoAmount?: number | null;
  distributedCryptoAmount?: number | null;
  reconciliationStatus?: string;
  timestamp: string;
  paidAt?: string | null;
  txHash?: string | null;
  settlementTxHash?: string | null;
  hasTrustedMerchantReturnUrl: boolean;
  preferredSessionId?: string | null;
  cryptoSwapOption?: PublicCryptoSwapOption | null;
  fiatOptions: PublicCheckoutOption[];
}

export interface PublicPaymentStatus {
  status: string;
  lifecycleStatus?: string;
  displayStatus: string;
  paymentSource: string;
  provider?: string;
  cryptoAmount?: number | null;
  receivedCryptoAmount?: number | null;
  distributedCryptoAmount?: number | null;
  reconciliationStatus?: string;
  cryptoCurrency?: string | null;
  paidAt?: string | null;
  txHash?: string | null;
  settlementTxHash?: string | null;
  disposableAddress?: string | null;
  merchantReferences?: MerchantReferences | null;
}

export interface PublicCheckoutLaunch {
  paymentStatus: PublicPaymentStatus;
  launchUrl: string;
  inlineUrl: string;
  supportsInlineEmbed: boolean;
}

export interface PublicCryptoSwapProxyRequest {
  method?: 'GET' | 'POST' | 'PUT';
  params?: AxiosRequestConfig['params'];
  data?: unknown;
}

class PublicPaymentService {
  private buildBuyerContextPayload(buyerContext?: PublicBuyerContext | null): PublicBuyerContext {
    const anonymousPayerId = anonymousPayerService.getOrCreate();
    return {
      ...(buyerContext ?? {}),
      ...(anonymousPayerId ? { anonymousPayerId } : {}),
    };
  }

  private buildPublicPaymentActionUrl(identifier: string, actionPath: string): string {
    const encodedIdentifier = encodeURIComponent(identifier.trim());
    return `${API_CONFIG.BASE_URL}/Transactions/public/${encodedIdentifier}/${actionPath}`;
  }

  private buildCryptoSwapProxyUrl(identifier: string, path: string): string {
    const normalizedPath = path.replace(/^\/+/, '');
    return this.buildPublicPaymentActionUrl(identifier, `crypto-swap/${normalizedPath}`);
  }

  getTrustedMerchantReturnActionUrl(identifier: string): string {
    return this.buildPublicPaymentActionUrl(identifier, 'merchant-return');
  }

  async getPaymentPage(identifier: string): Promise<PublicPaymentPage> {
    const response = await axios.get<PublicPaymentPage>(`${API_CONFIG.BASE_URL}/Transactions/public/${identifier}`);
    return response.data;
  }

  async getFiatOptions(identifier: string, buyerContext?: PublicBuyerContext | null): Promise<PublicFiatOptionsResult> {
    const response = await axios.post<PublicFiatOptionsResult>(
      `${API_CONFIG.BASE_URL}/Transactions/public/${identifier}/fiat-options`,
      this.buildBuyerContextPayload(buyerContext)
    );
    return response.data;
  }

  async reportFiatProviderIssue(
    identifier: string,
    issue: PublicFiatProviderIssueRequest,
    buyerContext?: PublicBuyerContext | null
  ): Promise<PublicFiatOptionsResult> {
    const response = await axios.post<PublicFiatOptionsResult>(
      `${API_CONFIG.BASE_URL}/Transactions/public/${identifier}/fiat-provider-issue`,
      {
        ...this.buildBuyerContextPayload(buyerContext),
        sessionId: issue.sessionId,
        provider: issue.provider,
        paymentMethod: issue.paymentMethod,
        reasonCode: issue.reasonCode ?? 'payer_reported_provider_issue',
      }
    );
    return response.data;
  }

  async getPaymentStatus(identifier: string): Promise<PublicPaymentStatus> {
    const response = await axios.get<PublicPaymentStatus>(`${API_CONFIG.BASE_URL}/Transactions/public/${identifier}/status`);
    return response.data;
  }

  async markDirectCryptoIntent(identifier: string): Promise<PublicPaymentStatus> {
    const response = await axios.post<PublicPaymentStatus>(`${API_CONFIG.BASE_URL}/Transactions/public/${identifier}/direct-intent`);
    return response.data;
  }

  async markCryptoSwapIntent(identifier: string): Promise<PublicPaymentStatus> {
    const response = await axios.post<PublicPaymentStatus>(`${API_CONFIG.BASE_URL}/Transactions/public/${identifier}/crypto-swap-intent`);
    return response.data;
  }

  async markFiatIntent(
    identifier: string,
    buyerContext?: PublicBuyerContext | null
  ): Promise<PublicPaymentStatus> {
    const response = await axios.post<PublicPaymentStatus>(
      `${API_CONFIG.BASE_URL}/Transactions/public/${identifier}/fiat-intent`,
      this.buildBuyerContextPayload(buyerContext)
    );
    return response.data;
  }

  async markFiatProviderIntent(
    identifier: string,
    sessionId: string,
    buyerContext?: PublicBuyerContext | null
  ): Promise<PublicCheckoutLaunch> {
    const response = await axios.post<PublicCheckoutLaunch>(
      `${API_CONFIG.BASE_URL}/Transactions/public/${identifier}/fiat-intent/${sessionId}`,
      this.buildBuyerContextPayload(buyerContext)
    );
    return response.data;
  }

  async proxyCryptoSwap<TResponse>(
    identifier: string,
    path: string,
    request?: PublicCryptoSwapProxyRequest
  ): Promise<TResponse> {
    const response = await axios.request<TResponse>({
      method: request?.method ?? 'GET',
      url: this.buildCryptoSwapProxyUrl(identifier, path),
      params: request?.params,
      data: request?.data,
    });
    return response.data;
  }

  async getSimpleSwapEstimate(
    identifier: string,
    query: SimpleSwapEstimateQuery
  ): Promise<SimpleSwapApiEnvelope<SimpleSwapEstimateResult>> {
    return this.proxyCryptoSwap<SimpleSwapApiEnvelope<SimpleSwapEstimateResult>>(identifier, 'v3/estimates', {
      method: 'GET',
      params: query,
    });
  }

  async getSimpleSwapCurrencies(
    identifier: string
  ): Promise<SimpleSwapApiEnvelope<SimpleSwapCurrencyResult[]>> {
    return this.proxyCryptoSwap<SimpleSwapApiEnvelope<SimpleSwapCurrencyResult[]>>(identifier, 'v3/currencies', {
      method: 'GET',
    });
  }

  async getSimpleSwapRanges(
    identifier: string,
    query: SimpleSwapRangeQuery
  ): Promise<SimpleSwapApiEnvelope<SimpleSwapRangeResult>> {
    return this.proxyCryptoSwap<SimpleSwapApiEnvelope<SimpleSwapRangeResult>>(identifier, 'v3/ranges', {
      method: 'GET',
      params: query,
    });
  }

  async checkSimpleSwapAvailability(
    identifier: string,
    query: SimpleSwapAvailabilityQuery
  ): Promise<SimpleSwapApiEnvelope<SimpleSwapAvailabilityResult>> {
    return this.proxyCryptoSwap<SimpleSwapApiEnvelope<SimpleSwapAvailabilityResult>>(identifier, 'v3/exchanges/check', {
      method: 'GET',
      params: query,
    });
  }

  async createSimpleSwapExchange(
    identifier: string,
    request: SimpleSwapCreateExchangeRequest
  ): Promise<SimpleSwapApiEnvelope<SimpleSwapExchangeResult>> {
    return this.proxyCryptoSwap<SimpleSwapApiEnvelope<SimpleSwapExchangeResult>>(identifier, 'v3/exchanges', {
      method: 'POST',
      data: request,
    });
  }

  async getSimpleSwapExchange(
    identifier: string,
    publicId: string
  ): Promise<SimpleSwapApiEnvelope<SimpleSwapExchangeResult>> {
    const encodedPublicId = encodeURIComponent(publicId);
    return this.proxyCryptoSwap<SimpleSwapApiEnvelope<SimpleSwapExchangeResult>>(identifier, `v3/exchanges/${encodedPublicId}`, {
      method: 'GET',
    });
  }
}

export const publicPaymentService = new PublicPaymentService();
